<template>
    <v-card class="diff-detail-panel" elevation="4" :style="panelStyle">
        <!-- Drag handle -->
        <div class="drag-handle" @mousedown.prevent="startResize">
            <div class="drag-handle-pill"></div>
        </div>
        <!-- Header -->
        <v-card-title class="panel-header d-flex align-center pa-3">
            <v-icon size="small" class="mr-2" :color="diffStateColor">
                {{ diffStateIcon }}
            </v-icon>
            <span class="panel-title">{{ nodeLabel }}</span>
            <v-chip size="x-small" :color="diffStateColor" variant="flat" class="ml-2 text-white">
                {{ diffStateText }}
            </v-chip>
            <v-spacer />
            <v-chip v-if="changeCount > 0" size="x-small" color="warning" variant="tonal" class="mr-2">
                {{ changeCount }} change{{ changeCount > 1 ? 's' : '' }}
            </v-chip>
            <v-btn icon size="x-small" variant="text" @click="$emit('close')">
                <v-icon>mdi-close</v-icon>
            </v-btn>
        </v-card-title>

        <!-- Content -->
            <v-card-text class="panel-content pa-0">
                <!-- Node type info -->
                <div class="node-type-row pa-3 pb-1">
                    <v-chip size="small" variant="tonal" color="primary">
                        <v-icon start size="small">{{ nodeTypeIcon }}</v-icon>
                        {{ nodeTypeLabel }}
                    </v-chip>
                    <span class="text-caption text-grey ml-2">
                        Left: {{ leftNodeId?.substring(0, 8) }}... → Right: {{ rightNodeId?.substring(0, 8) }}...
                    </span>
                </div>

                <!-- Show all / changes only toggle -->
                <div class="d-flex align-center px-3 py-1">
                    <v-btn-toggle v-model="showMode" density="compact" mandatory divided variant="outlined" class="filter-toggle">
                        <v-btn value="changes" size="small">
                            <v-icon start size="small">mdi-delta</v-icon>
                            Changes Only
                        </v-btn>
                        <v-btn value="all" size="small">
                            <v-icon start size="small">mdi-format-list-bulleted</v-icon>
                            Show All
                        </v-btn>
                    </v-btn-toggle>
                </div>

                <!-- Diff Table -->
                <div class="diff-table-container">
                    <table class="diff-table">
                        <thead>
                            <tr>
                                <th class="field-col">Field</th>
                                <th class="value-col left-col">Left (Base)</th>
                                <th class="value-col right-col">Right (Compare)</th>
                                <th class="status-col">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            <!-- Modified fields -->
                            <tr v-for="item in visibleModified" :key="'mod-' + item.field"
                                class="diff-row diff-row-modified">
                                <td class="field-col">
                                    <span class="field-name">{{ humanize(item.field) }}</span>
                                </td>
                                <td class="value-col left-col">
                                    <div class="value-cell-wrap">
                                        <div class="value-cell value-removed" v-html="renderLeftDiff(item.left, item.right)"></div>
                                        <v-btn icon variant="text" size="x-small" class="copy-btn" @click="copyValue(item.left)" title="Copy left value">
                                            <v-icon size="14">mdi-content-copy</v-icon>
                                        </v-btn>
                                    </div>
                                </td>
                                <td class="value-col right-col">
                                    <div class="value-cell-wrap">
                                        <div class="value-cell value-added" v-html="renderRightDiff(item.left, item.right)"></div>
                                        <v-btn icon variant="text" size="x-small" class="copy-btn" @click="copyValue(item.right)" title="Copy right value">
                                            <v-icon size="14">mdi-content-copy</v-icon>
                                        </v-btn>
                                    </div>
                                </td>
                                <td class="status-col">
                                    <v-icon size="small" color="warning">mdi-pencil</v-icon>
                                </td>
                            </tr>

                            <!-- Left-only fields -->
                            <tr v-for="item in visibleLeftOnly" :key="'lo-' + item.field"
                                class="diff-row diff-row-removed">
                                <td class="field-col">
                                    <span class="field-name">{{ humanize(item.field) }}</span>
                                </td>
                                <td class="value-col left-col">
                                    <div class="value-cell-wrap">
                                        <div class="value-cell value-removed">{{ formatVal(item.value) }}</div>
                                        <v-btn icon variant="text" size="x-small" class="copy-btn" @click="copyValue(item.value)" title="Copy value">
                                            <v-icon size="14">mdi-content-copy</v-icon>
                                        </v-btn>
                                    </div>
                                </td>
                                <td class="value-col right-col">
                                    <div class="value-cell value-empty">(not set)</div>
                                </td>
                                <td class="status-col">
                                    <v-icon size="small" color="error">mdi-minus-circle</v-icon>
                                </td>
                            </tr>

                            <!-- Right-only fields -->
                            <tr v-for="item in visibleRightOnly" :key="'ro-' + item.field"
                                class="diff-row diff-row-added">
                                <td class="field-col">
                                    <span class="field-name">{{ humanize(item.field) }}</span>
                                </td>
                                <td class="value-col left-col">
                                    <div class="value-cell value-empty">(not set)</div>
                                </td>
                                <td class="value-col right-col">
                                    <div class="value-cell-wrap">
                                        <div class="value-cell value-added">{{ formatVal(item.value) }}</div>
                                        <v-btn icon variant="text" size="x-small" class="copy-btn" @click="copyValue(item.value)" title="Copy value">
                                            <v-icon size="14">mdi-content-copy</v-icon>
                                        </v-btn>
                                    </div>
                                </td>
                                <td class="status-col">
                                    <v-icon size="small" color="success">mdi-plus-circle</v-icon>
                                </td>
                            </tr>

                            <!-- Unchanged fields -->
                            <tr v-for="field in visibleUnchanged" :key="'uc-' + field"
                                class="diff-row diff-row-unchanged">
                                <td class="field-col">
                                    <span class="field-name text-grey">{{ humanize(field) }}</span>
                                </td>
                                <td class="value-col left-col" colspan="2">
                                    <div class="value-cell-wrap">
                                        <div class="value-cell text-grey-darken-1">{{ formatVal(getLeftValue(field)) }}</div>
                                        <v-btn icon variant="text" size="x-small" class="copy-btn" @click="copyValue(getLeftValue(field))" title="Copy value">
                                            <v-icon size="14">mdi-content-copy</v-icon>
                                        </v-btn>
                                    </div>
                                </td>
                                <td class="status-col">
                                    <v-icon size="small" color="grey">mdi-check</v-icon>
                                </td>
                            </tr>

                            <!-- Empty state -->
                            <tr v-if="totalFields === 0">
                                <td colspan="4" class="text-center pa-4 text-grey">
                                    No data to compare
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </v-card-text>
    </v-card>
</template>

<script>
import { formatDiffValue, humanizeFieldName, inlineDiffLeft, inlineDiffRight } from '@/utils/flowDiffEngine';
import { getNodeTypeLabel, NODE_TYPE_INFO } from '@/utils/flowUtils';

export default {
    name: 'DiffDetailPanel',
    props: {
        /** Diff result from diffNodeData */
        diffResult: { type: Object, default: () => ({}) },
        /** Left node object */
        leftNode: { type: Object, default: null },
        /** Right node object */
        rightNode: { type: Object, default: null },
        /** Diff state: 'added', 'removed', 'modified', 'unchanged' */
        diffState: { type: String, default: 'unchanged' },
    },
    emits: ['close'],
    data() {
        return {
            showMode: 'changes',
            copiedField: false,
            // Resize state
            panelHeight: null,
            resizing: false,
            resizeStartY: 0,
            resizeStartH: 0,
        };
    },
    mounted() {
        // Default to 50% of viewport height
        this.panelHeight = Math.round(window.innerHeight * 0.5);
    },
    computed: {
        panelStyle() {
            if (!this.panelHeight) return { height: '50vh', maxHeight: '50vh' };
            return { height: this.panelHeight + 'px', maxHeight: this.panelHeight + 'px' };
        },
        nodeType() {
            return this.leftNode?.data?.type || this.rightNode?.data?.type || 'unknown';
        },
        nodeLabel() {
            return this.leftNode?.data?.label || this.rightNode?.data?.label || this.nodeTypeLabel;
        },
        nodeTypeLabel() {
            return getNodeTypeLabel(this.nodeType);
        },
        nodeTypeIcon() {
            return NODE_TYPE_INFO[this.nodeType]?.icon || 'mdi-help-circle';
        },
        leftNodeId() {
            return this.leftNode?.id || '';
        },
        rightNodeId() {
            return this.rightNode?.id || this.diffResult?.rightId || '';
        },
        changeCount() {
            return this.diffResult?.changeCount || 0;
        },
        diffStateColor() {
            const colors = { added: 'success', removed: 'error', modified: 'warning', unchanged: 'grey' };
            return colors[this.diffState] || 'grey';
        },
        diffStateIcon() {
            const icons = {
                added: 'mdi-plus-circle',
                removed: 'mdi-minus-circle',
                modified: 'mdi-pencil-circle',
                unchanged: 'mdi-check-circle'
            };
            return icons[this.diffState] || 'mdi-help-circle';
        },
        diffStateText() {
            const texts = { added: 'Added', removed: 'Removed', modified: 'Modified', unchanged: 'Unchanged' };
            return texts[this.diffState] || 'Unknown';
        },
        visibleModified() {
            return this.diffResult?.modified || [];
        },
        visibleLeftOnly() {
            return this.diffResult?.leftOnly || [];
        },
        visibleRightOnly() {
            return this.diffResult?.rightOnly || [];
        },
        visibleUnchanged() {
            if (this.showMode === 'all') {
                return this.diffResult?.unchanged || [];
            }
            return [];
        },
        totalFields() {
            return (this.diffResult?.modified?.length || 0)
                + (this.diffResult?.leftOnly?.length || 0)
                + (this.diffResult?.rightOnly?.length || 0)
                + (this.diffResult?.unchanged?.length || 0);
        }
    },
    methods: {
        // ---- Resize ----
        startResize(e) {
            this.resizing = true;
            this.resizeStartY = e.clientY;
            this.resizeStartH = this.panelHeight || this.$el.offsetHeight;
            document.addEventListener('mousemove', this.onResize);
            document.addEventListener('mouseup', this.stopResize);
            document.body.style.cursor = 'ns-resize';
            document.body.style.userSelect = 'none';
        },
        onResize(e) {
            if (!this.resizing) return;
            const dy = this.resizeStartY - e.clientY; // dragging up = positive
            const maxH = window.innerHeight - 120;
            this.panelHeight = Math.max(160, Math.min(maxH, this.resizeStartH + dy));
        },
        stopResize() {
            this.resizing = false;
            document.removeEventListener('mousemove', this.onResize);
            document.removeEventListener('mouseup', this.stopResize);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        },
        humanize(name) {
            return humanizeFieldName(name);
        },
        formatVal(value) {
            return formatDiffValue(value, 300);
        },
        getLeftValue(field) {
            return this.leftNode?.data?.[field];
        },
        renderLeftDiff(leftVal, rightVal) {
            return inlineDiffLeft(leftVal, rightVal);
        },
        renderRightDiff(leftVal, rightVal) {
            return inlineDiffRight(leftVal, rightVal);
        },
        async copyValue(value) {
            const text = (value === null || value === undefined)
                ? ''
                : (typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value));
            try {
                await navigator.clipboard.writeText(text);
                this.copiedField = true;
                setTimeout(() => { this.copiedField = false; }, 1500);
            } catch {
                // fallback
                const ta = document.createElement('textarea');
                ta.value = text;
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
                this.copiedField = true;
                setTimeout(() => { this.copiedField = false; }, 1500);
            }
        },
    },
    beforeUnmount() {
        document.removeEventListener('mousemove', this.onResize);
        document.removeEventListener('mouseup', this.stopResize);
    },
};
</script>

<style scoped>
.diff-detail-panel {
    border-radius: 12px 12px 0 0 !important;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05) !important;
    position: relative;
}

/* Drag handle bar */
.drag-handle {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 0 2px;
    cursor: ns-resize;
    flex-shrink: 0;
    background: linear-gradient(180deg, #f1f5f9 0%, #fff 100%);
    border-bottom: 1px solid #f1f5f9;
    transition: background 0.15s;
}

.drag-handle:hover {
    background: linear-gradient(180deg, #e8ecf1 0%, #f8fafc 100%);
}

.drag-handle:hover .drag-handle-pill {
    background: #94a3b8;
    width: 48px;
}

.drag-handle-pill {
    width: 36px;
    height: 4px;
    border-radius: 2px;
    background: #cbd5e1;
    transition: width 0.2s, background 0.2s;
}

.panel-header {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-bottom: 1px solid #e2e8f0;
    min-height: 48px;
}

.panel-title {
    font-size: 0.9rem;
    font-weight: 600;
    color: #334155;
}

.panel-content {
    overflow-y: auto;
    flex: 1;
}

.node-type-row {
    background: #fafbfc;
    border-bottom: 1px solid #f1f5f9;
}

.filter-toggle {
    border-radius: 8px !important;
}

.filter-toggle .v-btn {
    text-transform: none !important;
    font-size: 0.75rem !important;
}

/* Diff Table */
.diff-table-container {
    overflow-x: auto;
}

.diff-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8rem;
}

.diff-table thead th {
    background: #f8fafc;
    padding: 8px 12px;
    text-align: left;
    font-weight: 600;
    color: #64748b;
    border-bottom: 2px solid #e2e8f0;
    position: sticky;
    top: 0;
    z-index: 1;
}

.diff-table tbody tr {
    border-bottom: 1px solid #f1f5f9;
}

.diff-table tbody tr:hover {
    background: rgba(59, 130, 246, 0.02);
}

.field-col {
    width: 25%;
    padding: 8px 12px;
    vertical-align: top;
}

.value-col {
    width: 30%;
    padding: 8px 12px;
    vertical-align: top;
}

.status-col {
    width: 15%;
    padding: 8px 12px;
    text-align: center;
    vertical-align: top;
}

.field-name {
    font-weight: 500;
    color: #475569;
}

.value-cell {
    font-family: 'Fira Code', 'Cascadia Code', monospace;
    font-size: 0.75rem;
    word-break: break-word;
    white-space: pre-wrap;
    min-height: 22px;
    /* max-height: 200px; */
    overflow-y: auto;
    padding: 4px 8px;
    border-radius: 6px;
}

.value-added {
    background: rgba(34, 197, 94, 0.08);
    border-left: 3px solid #22c55e;
}

.value-removed {
    background: rgba(239, 68, 68, 0.08);
    border-left: 3px solid #ef4444;
}

/* Value cell with copy button */
.value-cell-wrap {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 4px;
}

.value-cell-wrap .value-cell {
    flex: 1;
    min-width: 0;
}

.copy-btn {
    flex-shrink: 0;
    opacity: 0;
    transition: opacity 0.15s;
    color: #64748b !important;
    margin-top: 2px;
}

.copy-btn:hover {
    color: #3b82f6 !important;
}

.value-cell-wrap:hover .copy-btn,
tr:hover .copy-btn {
    opacity: 1;
}

.value-empty {
    color: #94a3b8;
    font-style: italic;
}

.diff-row-modified {
    background: rgba(245, 158, 11, 0.03);
}

.diff-row-added {
    background: rgba(34, 197, 94, 0.03);
}

.diff-row-removed {
    background: rgba(239, 68, 68, 0.03);
}

.diff-row-unchanged {
    opacity: 0.6;
}

/* Git-style inline diff highlights */
.value-cell :deep(.diff-del) {
    background-color: rgba(239, 68, 68, 0.25);
    border-radius: 3px;
    padding: 1px 2px;
}

.value-cell :deep(.diff-add) {
    background-color: rgba(34, 197, 94, 0.25);
    border-radius: 3px;
    padding: 1px 2px;
}

/* Scrollbar */
.panel-content::-webkit-scrollbar {
    width: 6px;
}

.panel-content::-webkit-scrollbar-track {
    background: transparent;
}

.panel-content::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
}

.panel-content::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}
</style>
